class Search {
    static SELECTORS = {
        searchButton: "#header-search",
        search: "#predictive-search",
        form: "form.search",
        input: "[type=search]",
        reset: "button[type=reset]",
        predictiveSearchResults: ".predictive-search-results-container"
    };

    private searchButton: HTMLElement;
    private searchBox: HTMLElement;
    private form: HTMLFormElement;
    private readonly input: HTMLInputElement;
    private predictiveSearchResults: HTMLElement;
    private debounceTimeout: ReturnType<typeof setTimeout>;

    private cachedResults: { [key: string]: string };
    private statusElement: HTMLElement;
    private loadingText: string;
    private resultsMaxHeight: number;

    private resetButton: HTMLButtonElement;

    constructor(searchBox: HTMLElement) {
        this.cachedResults = {};
        this.searchBox = searchBox;
        this.searchButton = document.querySelector(Search.SELECTORS.searchButton);
        this.resetButton = this.searchBox.querySelector(Search.SELECTORS.reset);
        this.predictiveSearchResults = this.searchBox.querySelector(Search.SELECTORS.predictiveSearchResults);
        this.form = this.searchBox.querySelector(Search.SELECTORS.form);
        this.input = this.form.querySelector(Search.SELECTORS.input) as HTMLInputElement;
        this.input.value = this.getQuery();
        this.initListeners();
    }

    initListeners() {
        this.searchButton.addEventListener("click", this.open.bind(this));
        this.resetButton.addEventListener("click", this.close.bind(this, [true, true]));
        this.form.addEventListener("submit", this.onFormSubmit.bind(this));

        this.input.addEventListener("input", () => {
            if (this.debounceTimeout) {
                clearTimeout(this.debounceTimeout);
            }
            this.debounceTimeout = setTimeout(() => {
                this.onChange();
            }, 300);
        });
        this.input.addEventListener("focus", this.onFocus.bind(this));
        this.input.addEventListener("focusout", this.onFocusOut.bind(this));
        this.searchBox.addEventListener("keyup", this.onKeyup.bind(this));
        this.searchBox.addEventListener("keydown", this.onKeydown.bind(this));
    }

    getQuery() {
        return this.input.value.trim();
    }

    onFormSubmit(event: Event) {
        if (!this.getQuery().length || this.searchBox.querySelector('[aria-selected="true"] a')) event.preventDefault();
    }

    onChange() {
        const searchTerm = this.getQuery();

        if (!searchTerm.length) {
            this.close(true, true);
            this.predictiveSearchResults.classList.remove("have-results");
            return;
        }

        this.predictiveSearchResults.classList.add("have-results");

        this.getSearchResults(searchTerm);
    }

    onFocus() {
        const searchTerm = this.getQuery();

        if (!searchTerm.length) return;

        if (this.searchBox.dataset.results === "true") {
            this.open();
        } else {
            this.getSearchResults(searchTerm);
        }
    }

    onFocusOut() {
        setTimeout(() => {
            if (!this.searchBox.contains(document.activeElement)) this.close();
        });
    }

    onKeyup(event: KeyboardEvent) {
        if (!this.getQuery().length) {
            this.close(true, true);
        }
        event.preventDefault();
        switch (event.code) {
            case "ArrowUp":
                this.switchOption("up");
                break;
            case "ArrowDown":
                this.switchOption("down");
                break;
            case "Enter":
                this.selectOption();
                break;
        }
    }

    onKeydown(event: KeyboardEvent) {
        // Prevent the cursor from moving in the input when using the up and down arrow keys
        if (event.code === "ArrowUp" || event.code === "ArrowDown") {
            event.preventDefault();
        }
    }

    switchOption(direction: string) {
        if (!this.searchBox.dataset.hasOwnProperty("open")) return;

        const moveUp = direction === "up";
        const selectedElement = this.searchBox.querySelector('[aria-selected="true"]');
        const allElements = this.searchBox.querySelectorAll("li");
        let activeElement: Element = this.searchBox.querySelector("li");

        if (moveUp && !selectedElement) return;

        this.statusElement.textContent = "";

        if (!moveUp && selectedElement) {
            activeElement = selectedElement.nextElementSibling || allElements[0];
        } else if (moveUp) {
            activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
        }

        if (activeElement === selectedElement) return;

        activeElement.setAttribute("aria-selected", "true");
        if (selectedElement) selectedElement.setAttribute("aria-selected", "false");

        this.setLiveRegionText(activeElement.textContent);
        this.input.setAttribute("aria-activedescendant", activeElement.id);
    }

    selectOption() {
        const selectedProduct = this.searchBox.querySelector(
            '[aria-selected="true"] a, [aria-selected="true"] button'
        ) as HTMLElement;

        if (selectedProduct) selectedProduct.click();
    }

    getSearchResults(searchTerm: string) {
        const queryKey = searchTerm.replace(" ", "-").toLowerCase();
        this.setLiveRegionLoadingState();

        if (this.cachedResults[queryKey]) {
            this.renderSearchResults(this.cachedResults[queryKey]);
            return;
        }

        const url = this.getSearchUrl(searchTerm);

        fetch(url.toString())
            .then((response) => {
                if (!response.ok) {
                    const error = new Error(response.status as any);
                    this.close();
                    throw error;
                }
                return response.text();
            })
            .then((text) => {
                const resultsMarkup = new DOMParser()
                    .parseFromString(text, "text/html")
                    .querySelector("#shopify-section-predictive-search").innerHTML;
                this.cachedResults[queryKey] = resultsMarkup;
                this.renderSearchResults(resultsMarkup);
            })
            .catch((error) => {
                this.close();
                throw error;
            });
    }

    getSearchUrl(searchTerm: string) {
        const options = {
            type: "product",
            limit: 5,
            options: {
                fields: "variants.sku,variants.title,product_type,vendor,title,tag"
            }
        };

        let urlSearchParams = "";

        for (const [key, value] of Object.entries(options)) {
            if (typeof value === "object") {
                for (const [key2, value2] of Object.entries(value)) {
                    urlSearchParams += `resources[${key}][${key2}]=${value2}&`;
                }
                continue;
            }
            urlSearchParams += `resources[${key}]=${value}&`;
        }

        const url = new URL(window.location.origin + window.routes.predictive_search_url);
        url.search = new URLSearchParams(urlSearchParams).toString();
        url.searchParams.append("q", searchTerm);
        url.searchParams.append("section_id", "predictive-search");

        return url.toString();
    }

    setLiveRegionLoadingState() {
        this.statusElement = this.statusElement || this.searchBox.querySelector(".predictive-search-status");
        this.loadingText = this.loadingText || this.searchBox.getAttribute("data-loading-text");

        this.setLiveRegionText(this.loadingText);
        this.searchBox.dataset.loading = "true";
    }

    setLiveRegionText(statusText: string) {
        this.statusElement.setAttribute("aria-hidden", "false");
        this.statusElement.textContent = statusText;
        console.log(statusText);

        setTimeout(() => {
            this.statusElement.setAttribute("aria-hidden", "true");
        }, 1000);
    }

    renderSearchResults(resultsMarkup: string) {
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.searchBox.dataset.results = "true";

        this.setLiveRegionResults();
        this.open();
    }

    setLiveRegionResults() {
        this.searchBox.dataset.hasOwnProperty("loading") && this.searchBox.removeAttribute("data-loading");
        this.setLiveRegionText(
            this.searchBox.querySelector("[data-predictive-search-live-region-count-value]").textContent
        );
    }

    getResultsMaxHeight() {
        this.resultsMaxHeight =
            window.innerHeight - document.getElementById("shopify-section-header").getBoundingClientRect().bottom;
        return this.resultsMaxHeight;
    }

    open(event: MouseEvent = null) {
        if (event) {
            event.preventDefault();
            this.searchBox.classList.toggle("open");
            this.input.focus();
        }

        this.predictiveSearchResults.style.maxHeight = `${this.getResultsMaxHeight()}px`;
        this.searchBox.dataset.open = "";
        this.input.setAttribute("aria-expanded", "true");
    }

    close(clearSearchTerm = false, resetButton = false) {
        if (!resetButton) {
            this.searchBox.classList.remove("open");
        }
        if (clearSearchTerm) {
            this.input.value = "";
            this.input.removeAttribute("data-query");
            this.searchBox.removeAttribute("data-results");
        }

        this.input.setAttribute("aria-activedescendant", "");
        this.resultsMaxHeight = 0;
        this.predictiveSearchResults.removeAttribute("style");
        this.predictiveSearchResults.classList.remove("have-results");
    }
}

const searchBoxes = document.querySelectorAll(Search.SELECTORS.search) as NodeListOf<HTMLElement>;
if (searchBoxes.length) {
    for (const search of searchBoxes) {
        new Search(search);
    }
}
